import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

interface FlyingItem {
  id: string;
  src: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

interface FlyToCartContextType {
  flyToCart: (imageSrc: string, startElement: HTMLElement) => void;
  flyMultipleToCart: (items: { src: string; startElement: HTMLElement }[]) => void;
}

const FlyToCartContext = createContext<FlyToCartContextType | undefined>(undefined);

const getCartIconPosition = () => {
  const cartIcon = document.querySelector('[aria-label="Cart"]');
  if (!cartIcon) return { x: window.innerWidth - 60, y: 30 };
  const rect = cartIcon.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
};

export const FlyToCartProvider = ({ children }: { children: ReactNode }) => {
  const [flyingItems, setFlyingItems] = useState<FlyingItem[]>([]);

  const flyToCart = useCallback((imageSrc: string, startElement: HTMLElement) => {
    const rect = startElement.getBoundingClientRect();
    const end = getCartIconPosition();
    const id = `fly-${Date.now()}-${Math.random()}`;
    setFlyingItems(prev => [...prev, {
      id, src: imageSrc,
      startX: rect.left + rect.width / 2,
      startY: rect.top + rect.height / 2,
      endX: end.x, endY: end.y,
    }]);
    setTimeout(() => setFlyingItems(prev => prev.filter(i => i.id !== id)), 800);
  }, []);

  const flyMultipleToCart = useCallback((items: { src: string; startElement: HTMLElement }[]) => {
    const end = getCartIconPosition();
    items.forEach((item, index) => {
      setTimeout(() => {
        const rect = item.startElement.getBoundingClientRect();
        const id = `fly-${Date.now()}-${index}`;
        setFlyingItems(prev => [...prev, {
          id, src: item.src,
          startX: rect.left + rect.width / 2,
          startY: rect.top + rect.height / 2,
          endX: end.x, endY: end.y,
        }]);
        setTimeout(() => setFlyingItems(prev => prev.filter(i => i.id !== id)), 800);
      }, index * 100); // stagger
    });
  }, []);

  return (
    <FlyToCartContext.Provider value={{ flyToCart, flyMultipleToCart }}>
      {children}
      {createPortal(
        <AnimatePresence>
          {flyingItems.map(item => (
            <motion.img
              key={item.id}
              src={item.src}
              className="fixed pointer-events-none z-[9999] rounded-full object-cover shadow-lg border-2 border-primary/30"
              initial={{ x: item.startX - 25, y: item.startY - 25, width: 50, height: 50, opacity: 1 }}
              animate={{ x: item.endX - 12, y: item.endY - 12, width: 24, height: 24, opacity: 0.3 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            />
          ))}
        </AnimatePresence>,
        document.body
      )}
    </FlyToCartContext.Provider>
  );
};

export const useFlyToCart = () => {
  const ctx = useContext(FlyToCartContext);
  if (!ctx) throw new Error("useFlyToCart must be used within FlyToCartProvider");
  return ctx;
};
